import { DirectionProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';

import useNetworkStatus from '@shared/hook/useGetNetworkStatus';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import 'mantine-react-table/styles.css';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import Script from 'next/script';
import NextNProgress from 'nextjs-progressbar';
import { useEffect, useRef } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import type { ReactElement, ReactNode } from 'react';
import type { NextPage } from 'next';
import { autoLogin } from '../reducers/auth';
import { AppDispatch, persistor, store } from '../stores';
import '../styles/globals.css';
import '@mantine/carousel/styles.css';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import getToken from '../utils/auth/getToken';
import ThemeProvider from './ThemeProvider';

const AutoLogin = () => {
  const dispatch: AppDispatch = useDispatch();
  const { networkStatus } = useNetworkStatus();
  useEffect(() => {
    const token = getToken();
    if (token) {
      if (networkStatus) dispatch(autoLogin(token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

// const FullPageLoader = () => (
//   <Stack h="100vh" w="100vh" justify="center" align="center">
//     <LoadingOverlay visible />
//   </Stack>
// );

const GA_MEASUREMENT_ID = 'G-288N6VNF93';

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = Component.getLayout ?? ((page) => page);

  const queryClientRef = useRef(
    new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          retry: 0,
          networkMode: 'always',
          staleTime: 15 * 1000,
        },
        mutations: {
          retry: 0,
          networkMode: 'always',
        },
      },
    })
  );

  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      </Head>

      <Script src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`} strategy="afterInteractive" />

      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
        
          gtag('config', '${GA_MEASUREMENT_ID}}');        
        `}
      </Script>
      <Script src="https://sentry.hamravesh.com/js-sdk-loader/81423a92103066dcb4a2ed0a6cb2faa7.min.js" />
      {process.env.NODE_ENV !== 'development' && (
        <Script id="google-tag-manager" strategy="afterInteractive">
          {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-WSQ3TGDP');
      `}
        </Script>
      )}

      {/* <Script id="in-track">
        {`
          var inTrack_config = {
            app_key: 'AAAAlw',
            auth_key: '84d71af3-ef0e-4ee4-8401-5c3a1b145314',
            public_key: 'BIEKHbzLrH8wuQLACCff59xBg5Fuk3p7b6MGabXXgMvAXqafW7RL9xCc56fcRvllPQvPu4DobhAwGnJEnJbsB-M='
          };
          (function (i, n, t, r, a, c, k) {
            o = i['InTrack'] = i['InTrack'] || {}; i[a] = i[a] || function () { (o.q = o.q || []).push(arguments); };
            s = n.createElement(t); s.async = true; s.src = r; s.onload = function () { o.init(c) };
            e = n.getElementsByTagName(t)[0]; e.parentNode.insertBefore(s, k);
          })(window, document, 'script', "//static1.intrack.ir/api/web/download/sdk/v1/inTrack.min.js?v=00",
          "Intk", inTrack_config);
        `}
      </Script> */}

      <Provider store={store}>
        <DirectionProvider>
          <ThemeProvider vendor={pageProps.vendor}>
            <PersistGate persistor={persistor}>
              <QueryClientProvider client={queryClientRef.current}>
                <div dir="rtl">
                  <AutoLogin />
                  <Notifications position="top-center" />
                  <NextNProgress color="#00afaa" />
                  {getLayout(<Component {...pageProps} />)}
                </div>
                {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
              </QueryClientProvider>
            </PersistGate>
          </ThemeProvider>
        </DirectionProvider>
      </Provider>
    </>
  );
};

export default MyApp;
